import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { LogIn, Plus, Pencil, Trash2, Eye, AlertTriangle, Stethoscope } from 'lucide-react';
import { ENV_META, EnvBadge, PermBadge } from './helpers';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: '', token_value: '', nip: '', environment: 'prod',
  permissions: ['upload', 'download'], assigned_user_id: '', active: true,
};

export const TokensTab = ({ tokens, users, onTokensChanged, onAuthenticate, authenticating }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showTokenValue, setShowTokenValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diagnosing, setDiagnosing] = useState(null); // token_id while running
  const [diagnoseDialog, setDiagnoseDialog] = useState(null); // { token, result }

  const handleDiagnose = async (tok) => {
    setDiagnosing(tok.id);
    try {
      const r = await axios.post(`${API}/api/ksef/tokens/${tok.id}/diagnose?force_reauth=true`);
      setDiagnoseDialog({ token: tok, result: r.data });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Diagnose failed';
      toast.error(msg, { duration: 8000 });
    } finally {
      setDiagnosing(null);
    }
  };

  const openCreate = () => {
    setEditingToken(null);
    setForm(EMPTY_FORM);
    setShowTokenValue(false);
    setDialogOpen(true);
  };

  const openEdit = (tok) => {
    setEditingToken(tok);
    setForm({
      name: tok.name, token_value: '', nip: tok.nip,
      environment: tok.environment, permissions: tok.permissions || [],
      assigned_user_id: tok.assigned_user_id || '', active: tok.active,
    });
    setShowTokenValue(false);
    setDialogOpen(true);
  };

  const togglePermission = (p) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(p) ? f.permissions.filter(x => x !== p) : [...f.permissions, p],
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.nip) { toast.error(t('auto.misc2.nameAndNipAreRequired')); return; }
    if (!editingToken && !form.token_value) { toast.error(t('auto.misc2.tokenValueIsRequired')); return; }
    if (!form.permissions.length) { toast.error(t('auto.misc2.selectAtLeastOnePermission')); return; }
    setSaving(true);
    try {
      const payload = { ...form, assigned_user_id: form.assigned_user_id || null };
      if (editingToken) {
        if (!payload.token_value) delete payload.token_value;
        await axios.put(`${API}/api/ksef/tokens/${editingToken.id}`, payload);
        toast.success(t('auto.misc2.tokenUpdated'));
      } else {
        await axios.post(`${API}/api/ksef/tokens`, payload);
        toast.success(t('auto.misc2.tokenCreated'));
      }
      setDialogOpen(false);
      onTokensChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save token');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete token "${name}"?`)) return;
    try {
      await axios.delete(`${API}/api/ksef/tokens/${id}`);
      toast.success(t('auto.misc2.tokenDeleted'));
      onTokensChanged();
    } catch { toast.error(t('auto.misc2.failedToDeleteToken')); }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm">{t('auto.misc2.ksefTokenManagement')}</CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('auto.misc2.tokensAreEnvironmentSpecificAProduction')}
              </p>
            </div>
            <Button size="sm" onClick={openCreate} data-testid="create-token-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />{t('auto.misc2.addToken')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Environment legend */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {Object.entries(ENV_META).map(([env, meta]) => (
              <div key={env} className={`text-xs px-3 py-1.5 rounded border ${meta.border} flex items-center gap-1.5`}>
                <span className={`text-[10px] font-bold px-1.5 rounded ${meta.color}`}>{meta.label}</span>
                <span className="text-gray-600">
                  {env === 'prod' ? 'Live system — irreversible' : env === 'demo' ? t('auto.misc2.testEnvironmentTemporary') : t('auto.misc2.localTest')}
                </span>
              </div>
            ))}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('auto.misc2.name')}</TableHead>
                <TableHead>{t('auto.misc2.nip')}</TableHead>
                <TableHead>{t('auto.misc2.environment')}</TableHead>
                <TableHead>{t('auto.misc2.permissions')}</TableHead>
                <TableHead>{t('auto.misc2.assignedTo')}</TableHead>
                <TableHead>{t('auto.misc2.session')}</TableHead>
                <TableHead>{t('auto.misc2.status')}</TableHead>
                <TableHead>{t('auto.misc2.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.length === 0
                ? <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-8">
                    {t('auto.misc2.noTokensConfiguredAddAToken')}
                  </TableCell></TableRow>
                : tokens.map(tok => (
                    <TableRow key={tok.id}>
                      <TableCell className="font-medium">{tok.name}</TableCell>
                      <TableCell className="font-mono text-xs">{tok.nip}</TableCell>
                      <TableCell><EnvBadge env={tok.environment} /></TableCell>
                      <TableCell><PermBadge perms={tok.permissions} /></TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {tok.assigned_user_id
                          ? (users.find(u => u.id === tok.assigned_user_id)?.username || tok.assigned_user_id)
                          : <span className="italic text-gray-400">{t('auto.misc2.adminOnly')}</span>
                        }
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs font-medium ${tok.session_active ? 'text-green-600' : 'text-gray-400'}`}>
                          {tok.session_active ? t('auto.misc2.active') : t('auto.misc2.inactive')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={tok.active ? 'default' : 'secondary'}>{tok.active ? t('auto.misc2.active2') : t('auto.misc2.disabled')}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => onAuthenticate(tok.id)} disabled={authenticating}
                            title={t('auto.misc2.authenticateUsingThisToken')} data-testid="token-auth-btn">
                            <LogIn className="h-3 w-3 mr-0.5" />{t('auto.misc2.auth')}
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => handleDiagnose(tok)} disabled={diagnosing === tok.id}
                            title={t('auto.misc2.diagnoseListThisTokenSActually')}
                            data-testid="token-diagnose-btn">
                            <Stethoscope className="h-3 w-3 mr-0.5" />
                            {diagnosing === tok.id ? '…' : 'Diagnose'}
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(tok)} title={t('auto.misc2.edit')} data-testid="token-edit-btn">
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600"
                            onClick={() => handleDelete(tok.id, tok.name)} title={t('auto.misc2.delete')} data-testid="token-delete-btn">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Token create/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingToken ? t('auto.misc2.editToken') : t('auto.misc2.addKsefToken')}</DialogTitle>
            <DialogDescription>
              {editingToken ? t('auto.misc2.updateTokenDetailsLeaveTokenValue') : t('auto.misc2.configureANewKsefAuthenticationToken')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('auto.misc2.name2')}</Label>
                <Input placeholder="e.g. Finance Production" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="token-name-input" />
              </div>
              <div>
                <Label className="text-xs">{t('auto.misc2.nip2')}</Label>
                <Input placeholder={t('auto.misc2.10Digits')} value={form.nip}
                  onChange={e => setForm(f => ({ ...f, nip: e.target.value }))} data-testid="token-nip-input" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Token Value {!editingToken ? '*' : '(leave blank to keep current)'}</Label>
              <div className="flex gap-1">
                <Input type={showTokenValue ? 'text' : 'password'}
                  placeholder={editingToken ? t('auto.misc2.leaveBlankToKeepCurrent') : t('auto.misc2.ksefAuthToken')}
                  value={form.token_value}
                  onChange={e => setForm(f => ({ ...f, token_value: e.target.value }))}
                  className="font-mono text-xs" data-testid="token-value-input" />
                <Button size="icon" variant="ghost" type="button" onClick={() => setShowTokenValue(v => !v)}>
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs">{t('auto.misc2.environment2')}</Label>
              <Select value={form.environment} onValueChange={v => setForm(f => ({ ...f, environment: v }))}>
                <SelectTrigger data-testid="token-env-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prod">
                    <div className="flex items-center gap-2"><span className="text-xs font-bold text-red-600">{t('auto.misc2.production')}</span><span className="text-xs text-gray-500">{t('auto.misc2.liveKsefSystem')}</span></div>
                  </SelectItem>
                  <SelectItem value="demo">
                    <div className="flex items-center gap-2"><span className="text-xs font-bold text-amber-600">{t('auto.misc2.demo')}</span><span className="text-xs text-gray-500">{t('auto.misc2.testEnvironment')}</span></div>
                  </SelectItem>
                  <SelectItem value="test">
                    <div className="flex items-center gap-2"><span className="text-xs font-bold text-slate-600">{t('auto.misc2.test')}</span><span className="text-xs text-gray-500">{t('auto.misc2.localTesting')}</span></div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {form.environment === 'prod' && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />{t('auto.misc2.thisTokenWillInteractWithThe')}
                </p>
              )}
              {form.environment === 'demo' && (
                <p className="text-xs text-amber-600 mt-1">{t('auto.misc2.demoEnvironmentSafeForTestingWill')}</p>
              )}
            </div>

            <div>
              <Label className="text-xs mb-1 block">{t('auto.misc2.permissions2')}</Label>
              <div className="flex gap-3">
                {['upload', 'download'].map(p => (
                  <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={form.permissions.includes(p)}
                      onCheckedChange={() => togglePermission(p)}
                      data-testid={`perm-${p}`}
                    />
                    <span className="text-sm capitalize">{p}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">{t('auto.misc2.assignToUserOptionalLeaveBlank')}</Label>
              <Select value={form.assigned_user_id || '__admin__'} onValueChange={v => setForm(f => ({ ...f, assigned_user_id: v === '__admin__' ? '' : v }))}>
                <SelectTrigger data-testid="token-user-select"><SelectValue placeholder={t('auto.misc2.adminOnlyNoAssignment')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__admin__">{t('auto.misc2.adminOnly')}</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.username} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: !!v }))} data-testid="token-active-check" />
              <Label className="text-xs cursor-pointer">{t('auto.misc2.tokenActive')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="dialog-cancel-btn">{t('auto.misc2.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-token-btn">
              {saving ? 'Saving…' : editingToken ? t('auto.misc2.updateToken') : t('auto.misc2.createToken')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diagnose result dialog */}
      <Dialog open={!!diagnoseDialog} onOpenChange={(o) => !o && setDiagnoseDialog(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="diagnose-dialog">
          <DialogHeader>
            <DialogTitle>Token diagnostic — {diagnoseDialog?.token?.name}</DialogTitle>
            <DialogDescription>
              What KSeF actually has registered for this token's context (NIP {diagnoseDialog?.token?.nip}).
            </DialogDescription>
          </DialogHeader>
          {diagnoseDialog?.result && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 rounded border bg-gray-50 p-2 text-xs">
                <div><span className="text-gray-500">{t('auto.misc2.environment3')}</span> <span className="font-mono">{diagnoseDialog.result.environment}</span></div>
                <div><span className="text-gray-500">{t('auto.misc2.apiUrl')}</span> <span className="font-mono break-all">{diagnoseDialog.result.api_url}</span></div>
                <div><span className="text-gray-500">{t('auto.misc2.nip3')}</span> <span className="font-mono">{diagnoseDialog.result.nip}</span></div>
                <div><span className="text-gray-500">{t('auto.misc2.session2')}</span> <span className="font-mono">{diagnoseDialog.result.session_reference || '—'}</span></div>
              </div>

              <div className={`rounded border px-3 py-2 ${diagnoseDialog.result.has_invoice_write ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
                <div className="font-semibold">
                  {diagnoseDialog.result.has_invoice_write
                    ? '✓ This token can issue invoices'
                    : '✗ This token cannot issue invoices'}
                </div>
                <div className="mt-1 text-xs">
                  {diagnoseDialog.result.has_invoice_write
                    ? `Token + entity both have invoice-write capability (matched: ${(diagnoseDialog.result.matched_token_permissions || []).join(', ')}).`
                    : (
                      <>
                        Issuing invoices requires <strong>both</strong> the token
                        and the entity to have <code>{t('auto.misc2.invoicewrite')}</code> (or
                        <code>{t('auto.misc2.pefinvoicewrite')}</code>/<code>{t('auto.misc2.enforcementoperations')}</code>).
                        See the two sections below.
                      </>
                    )
                  }
                </div>
              </div>

              {/* Token-level permissions (locked in at token creation) */}
              {diagnoseDialog.result.ksef_token_info && (
                <div>
                  <div className="font-semibold mb-1">
                    {t('auto.misc2.tokenLevelPermissionsLockedInAt')}
                    {' '}
                    <span className={`ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] ${diagnoseDialog.result.token_has_invoice_write ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                      {diagnoseDialog.result.token_has_invoice_write ? t('auto.misc2.ok') : t('auto.misc2.missingInvoicewrite')}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mb-1">
                    Author: <span className="font-mono">
                      {diagnoseDialog.result.ksef_token_info.authorIdentifier?.type}
                      {' '}
                      {diagnoseDialog.result.ksef_token_info.authorIdentifier?.value}
                    </span>
                    {' · Status: '}
                    <span className="font-mono">{diagnoseDialog.result.ksef_token_status}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(diagnoseDialog.result.ksef_token_permissions || []).map(p => (
                      <Badge key={p} variant={['InvoiceWrite','PefInvoiceWrite','EnforcementOperations'].includes(p) ? 'default' : 'secondary'} className="text-xs">{p}</Badge>
                    ))}
                  </div>
                  {!diagnoseDialog.result.token_has_invoice_write && (
                    <div className="mt-2 rounded bg-amber-50 border border-amber-200 px-2 py-1.5 text-xs text-amber-900">
                      <strong>{t('auto.misc2.whyInvoicewriteIsMissingOnThis')}</strong>{' '}
                      KSeF caps a token&apos;s permissions to whatever its
                      <em> author </em>
                      ({diagnoseDialog.result.ksef_token_info.authorIdentifier?.value})
                      could legally delegate at creation time — even if you
                      ticked &ldquo;all permissions&rdquo; in the Taxpayer App. The
                      author needs <code>{t('auto.misc2.invoicewrite')}</code> at the entity level
                      first, then create a <strong>new</strong> token (existing
                      tokens are immutable). To fix:
                      <ol className="list-decimal list-inside mt-1 space-y-0.5">
                        <li>{t('auto.misc2.open')} <a href="https://ksef-demo.mf.gov.pl" target="_blank" rel="noreferrer" className="underline">ksef-demo.mf.gov.pl</a> {t('auto.misc2.asTheEntityOwner')}</li>
                        <li>{t('auto.misc2.grant')} <code>{t('auto.misc2.invoicewrite')}</code> {t('auto.misc2.toPeselNip')} <span className="font-mono">{diagnoseDialog.result.ksef_token_info.authorIdentifier?.value}</span>.</li>
                        <li>{t('auto.misc2.haveThatPersonCreateANew')}</li>
                        <li>{t('auto.misc2.replaceTheTokenInThisApp')} <strong>{t('auto.misc2.diagnose')}</strong> {t('auto.misc2.toConfirm')}</li>
                      </ol>
                    </div>
                  )}
                </div>
              )}
              {diagnoseDialog.result.ksef_token_info_error && (
                <div className="text-xs text-amber-700 break-all">
                  /tokens/{'{ref}'} → {diagnoseDialog.result.ksef_token_info_error}
                </div>
              )}

              {/* Entity-level grants */}
              <div>
                <div className="font-semibold mb-1">
                  Entity-level grants (what NIP {diagnoseDialog.result.nip} has registered)
                  {' '}
                  <span className={`ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] ${diagnoseDialog.result.entity_has_invoice_write ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                    {diagnoseDialog.result.entity_has_invoice_write ? t('auto.misc2.ok') : t('auto.misc2.missingInvoicewrite')}
                  </span>
                </div>
                {(diagnoseDialog.result.granted_permission_codes || []).length === 0 ? (
                  <div className="rounded bg-amber-50 border border-amber-200 px-2 py-1.5 text-xs text-amber-900">
                    {t('auto.misc2.noPermissionsVisibleToThisCaller')}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {(diagnoseDialog.result.granted_permission_codes || []).map(p => (
                      <Badge key={p} variant={['InvoiceWrite','PefInvoiceWrite','EnforcementOperations'].includes(p) ? 'default' : 'secondary'} className="text-xs">{p}</Badge>
                    ))}
                  </div>
                )}
                {diagnoseDialog.result.personal_permissions_error && (
                  <div className="mt-1 text-xs text-red-700 break-all">
                    /permissions/query/personal/grants → {diagnoseDialog.result.personal_permissions_error}
                  </div>
                )}
              </div>

              <details className="text-xs">
                <summary className="cursor-pointer text-gray-600">{t('auto.misc2.rawResponse')}</summary>
                <pre className="mt-1 bg-gray-50 border rounded p-2 overflow-auto max-h-60">{JSON.stringify(diagnoseDialog.result, null, 2)}</pre>
              </details>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDiagnoseDialog(null)} data-testid="diagnose-close-btn">{t('auto.misc2.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
